import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomMUITable from "src/components/CustomMUITable";
import Label from "src/components/Label";
import { buildTrackerDashboardStats } from "./trackerDashboardStats";
import {
  getInitials,
  getSubmissionsForTracker,
  SUBMISSION_SOURCES,
} from "./submissionStorage";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";

const ACTIVITY_META = {
  active: { label: "Active", color: "success" },
  cooling: { label: "Cooling", color: "warning" },
  at_risk: { label: "Quiet", color: "error" },
  completed: { label: "Completed", color: "info" },
  dropped: { label: "Dropped", color: "default" },
  unknown: { label: "—", color: "default" },
};

const VERDICT_META = {
  up: {
    label: "Improving",
    color: "#047857",
    bg: "rgba(16,185,129,0.12)",
    Icon: TrendingUpIcon,
  },
  down: {
    label: "Declining",
    color: "#b91c1c",
    bg: "rgba(239,68,68,0.12)",
    Icon: TrendingDownIcon,
  },
  mixed: {
    label: "Mixed",
    color: "#b45309",
    bg: "rgba(245,158,11,0.14)",
    Icon: TrendingFlatIcon,
  },
  flat: {
    label: "Stable",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
  none: {
    label: "—",
    color: MUTED,
    bg: "#f1f5f9",
    Icon: TrendingFlatIcon,
  },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const StatCard = ({ label, value, hint, tone }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      minWidth: 110,
      p: 1.5,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      borderLeft: tone ? `3px solid ${tone}` : undefined,
      bgcolor: "#fff",
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography
      sx={{ mt: 0.4, fontSize: 22, fontWeight: 700, color: tone || "#0f172a" }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.35, fontSize: 11, color: MUTED }}>{hint}</Typography>
    ) : null}
  </Box>
);

export default function TrackerDetailPeopleTab({ tracker }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const stats = useMemo(() => {
    if (!tracker?.id) return null;
    return buildTrackerDashboardStats({
      trackers: [tracker],
      submissions: getSubmissionsForTracker(tracker.id),
    });
  }, [tracker]);

  const clients = stats?.clients || [];
  const kpis = stats?.kpis || {};

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      `${c.name} ${c.email} ${c.coachName} ${c.statusName} ${
        c.performance?.label || ""
      }`
        .toLowerCase()
        .includes(q)
    );
  }, [clients, search]);

  const TABLE_HEAD = [
    { id: "person", label: "Person" },
    { id: "performance", label: "Performance" },
    { id: "activity", label: "Activity" },
    { id: "progress", label: "Journey %" },
    { id: "coach", label: "Coach" },
    { id: "entries", label: "Entries", alignRight: true },
    { id: "last", label: "Last check-in" },
  ];

  const tableRows = filtered.map((c) => {
    const meta = ACTIVITY_META[c.activity] || ACTIVITY_META.unknown;
    const verdict = VERDICT_META[c.performance?.verdict] || VERDICT_META.none;
    const VerdictIcon = verdict.Icon;
    const primary = c.performance?.primary;
    return {
      id: c.key,
      ...c,
      person: (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: 12,
              fontWeight: 700,
              bgcolor:
                c.source === SUBMISSION_SOURCES.PUBLIC ? "#8b5cf6" : "#04abbb",
            }}
          >
            {getInitials(c.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              {c.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              {c.email ||
                (c.source === SUBMISSION_SOURCES.PUBLIC
                  ? "Public / BMS"
                  : "GymRite member")}
            </Typography>
          </Box>
        </Stack>
      ),
      performance: (
        <Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.4,
              px: 0.85,
              py: 0.3,
              borderRadius: "999px",
              bgcolor: verdict.bg,
              color: verdict.color,
              mb: primary ? 0.4 : 0,
            }}
          >
            <VerdictIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>
              {verdict.label}
            </Typography>
          </Box>
          {primary ? (
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              {primary.label}: {primary.currentText}{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: verdict.color }}
              >
                ({primary.deltaText})
              </Box>
            </Typography>
          ) : null}
        </Box>
      ),
      activity: (
        <Label variant="ghost" color={meta.color}>
          {meta.label}
        </Label>
      ),
      progress: (
        <Box sx={{ minWidth: 100 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontSize: 11, color: MUTED }}>
              {c.formsCount} forms
            </Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
              {c.progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={c.progress}
            sx={{
              mt: 0.4,
              height: 6,
              borderRadius: 99,
              bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                bgcolor: c.progress >= 70 ? "#10b981" : PRIMARY,
              },
            }}
          />
        </Box>
      ),
      coach: (
        <Typography sx={{ fontSize: 13 }}>{c.coachName || "—"}</Typography>
      ),
      entries: (
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
          {c.submissions}
        </Typography>
      ),
      last: (
        <Box>
          <Typography sx={{ fontSize: 13 }}>
            {c.lastAt ? moment(c.lastAt).format("DD MMM YYYY") : "—"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: MUTED }}>
            {c.daysSince === 0 ? "Today" : `${c.daysSince}d ago`}
          </Typography>
        </Box>
      ),
    };
  });

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            People on this journey
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Who is improving, slipping, or going quiet — click a row for detail.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search people…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ...fieldSx, width: { xs: "100%", sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        <StatCard label="People" value={clients.length} hint="Unique" />
        <StatCard
          label="Improving"
          value={kpis.improvingPeople || 0}
          hint="vs baseline"
          tone="#047857"
        />
        <StatCard
          label="Declining"
          value={kpis.decliningPeople || 0}
          hint="needs focus"
          tone={kpis.decliningPeople ? "#b91c1c" : undefined}
        />
      </Box>

      <CustomMUITable
        TABLE_HEAD={TABLE_HEAD}
        data={tableRows}
        MENU_OPTIONS={[]}
        className="card-with-background tracker-module-table"
        hide_search
        handleClick={(row) => {
          if (row.latestSubmissionId) {
            navigate(`/tracker-submissions/${row.latestSubmissionId}`);
          }
        }}
      />
    </Box>
  );
}
